import store from '@/store';
import checkProRegister from '@/utils/checkProRegister';
import { flatten } from '@/utils/collection';
import sleep from '@/utils/sleep';
import axios from 'axios';
import routes from './routes';

const publicRoutes = flatten(routes)
    .filter(({ meta }) => meta?.disallowAuthed)
    .map(({ name }) => name);

/**
 * Checks if user is authenticated
 *
 * @returns {Function} Callback for navigation guard
 */
export const userDataGuard = () => {
    return async (to, from, next) => {
        if (! store.state.user) {
            await axios.get('/sanctum/csrf-cookie');
            await store.dispatch('loadUser');

            if (! store.state.user && ! publicRoutes.includes(to.name)) {
                store.commit('setLoginRedirectUrl', to.path);
                next('/login');
                return;
            }
        }

        next();
    };
};

/**
 * Navigation guard for Dashboard view.
 *
 * @param {object} to Route being navigated.
 * @param {object} from Route Route being navigated from.
 * @param {Function} next Function called to resolve the quard.
 *
 * @returns {Function} Callback for navigation guard
 */
export const dashboardGuard = (to, from, next) => {
    const userData = store.state.user;

    if (userData) {
        return next();
    }

    if ('/' !== window.location.pathname) {
        store.commit('setLoginRedirectUrl', window.location.pathname);
    }

    next('/login');
};

/**
 * Callback for not authenticated users guard.
 *
 * @param {object} router Vue router instance.
 *
 * @returns {Function} Callback for navigation guard
 */
export const publicGuard = router => (to, from, next) => {
    const userData = store.state.user;

    if (userData && checkProRegister(to)) {
        const paymentInitialized = userData.meta_data.payment_initialized;

        if (paymentInitialized) {
            return next();
        }
    }

    if (userData && to.meta.disallowAuthed) {
        return router.push('/').catch(() => {});
    }

    return next();
};

/**
 * Show ID check for shows view
 *
 * @param {object} to Route being navigated.
 * @param {object} from Route Route being navigated from.
 * @param {Function} next Function called to resolve the quard.
 *
 */
export const showGuard = async (to, from, next) => {
    while (! store.state.shows) {
        /**
         * Loop breaks when shows will be loaded to store
         */
        await sleep(10);
    }

    if (undefined === store.state.shows[to.params.hash]) {
        next('/404');
    }

    next();
};

/**
 * Guard for frozen shows.
 *
 * @param {object} to Route being navigated.
 * @param {object} from Route Route being navigated from.
 * @param {Function} next Function called to resolve the quard.
 * @returns {Function} Callback for navigation guard
 */
export const frozenShowGuard = async (to, from, next) => {
    if (! to.path.startsWith('/shows') || '/shows' === to.path || 'Players' === to.name) {
        return next();
    }

    while (! store.state.shows) {
        await sleep(10);
    }

    const show = store.state.shows[to.params.hash];

    if (! show?.isFrozen) {
        return next();
    }

    next(`/shows/${to.params.hash}`);
};
